import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, GridIcon, ListIcon, ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, XIcon, StarIcon, HeartIcon, ShoppingBagIcon, TruckIcon, MapPinIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useDarkMode } from '../../context/DarkModeContext';
export interface MerchProduct {
  id: string;
  name: string;
  vendorName: string;
  vendorSlug: string;
  vendorImage?: string;
  image: string;
  price: number;
  rating: number;
  reviewCount: number;
  subcategory: string;
  tags: string[];
  description: string;
  shipping?: string;
  location?: string;
}
interface FilterOptions {
  subcategories: string[];
  priceRanges: {
    label: string;
    min: number;
    max: number | null;
  }[];
  vendors: string[];
}
interface MerchCategoryPageProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  products: MerchProduct[];
  filterOptions: FilterOptions;
}
type ViewMode = 'grid' | 'list';
const MerchCategoryPage: React.FC<MerchCategoryPageProps> = ({
  title,
  description,
  icon,
  products,
  filterOptions
}) => {
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedPriceRanges, setSelectedPriceRanges] = useState<string[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [expandedProductIds, setExpandedProductIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  // Sidebar states
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    subcategories: true,
    price: true,
    vendors: true
  });
  // Filter logic
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm === '' || product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSubcategory = selectedSubcategories.length === 0 || selectedSubcategories.includes(product.subcategory);
    const matchesVendor = selectedVendors.length === 0 || selectedVendors.includes(product.vendorName);
    const matchesPrice = selectedPriceRanges.length === 0 || selectedPriceRanges.some(rangeLabel => {
      const range = filterOptions.priceRanges.find(r => r.label === rangeLabel);
      if (!range) return false;
      if (range.max === null) return product.price >= range.min;
      return product.price >= range.min && product.price <= range.max;
    });
    return matchesSearch && matchesSubcategory && matchesVendor && matchesPrice;
  });
  // Handlers
  const toggleSubcategory = (subcategory: string) => {
    setSelectedSubcategories(prev => prev.includes(subcategory) ? prev.filter(c => c !== subcategory) : [...prev, subcategory]);
  };
  const togglePriceRange = (rangeLabel: string) => {
    setSelectedPriceRanges(prev => prev.includes(rangeLabel) ? prev.filter(r => r !== rangeLabel) : [...prev, rangeLabel]);
  };
  const toggleVendor = (vendor: string) => {
    setSelectedVendors(prev => prev.includes(vendor) ? prev.filter(v => v !== vendor) : [...prev, vendor]);
  };
  const toggleProductExpand = (id: string) => {
    setExpandedProductIds(prev => prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]);
  };
  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
    document.body.style.overflow = !mobileFiltersOpen ? 'hidden' : 'auto';
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const resetFilters = () => {
    setSelectedSubcategories([]);
    setSelectedPriceRanges([]);
    setSelectedVendors([]);
    setSearchTerm('');
  };
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  // Collapsible Section Component
  const CollapsibleSection = ({
    title,
    sectionKey,
    children
  }: {
    title: string;
    sectionKey: keyof typeof expandedSections;
    children: React.ReactNode;
  }) => <div className={`border-b py-4 last:border-b-0 ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-2872" data-mp="1szkmz8sasy4i">
      <button onClick={() => toggleSection(sectionKey)} className="flex w-full items-center justify-between text-left" data-id="element-2873" data-mp="8h0d1qdxoww2">
        <h3 className={`font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-2874" data-mp="dftq5hpb6lvp">
          {title}
        </h3>
        {expandedSections[sectionKey] ? <ChevronUpIcon className={`h-4 w-4 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2875" data-mp="3ph6drpbiwqm" /> : <ChevronDownIcon className={`h-4 w-4 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2876" data-mp="3lyhodgb9r42h" />}
      </button>
      {expandedSections[sectionKey] && <div className="mt-3 space-y-2" data-id="element-2877" data-mp="3cxjnv801heon">{children}</div>}
    </div>;
  const FilterContent = () => <div className="space-y-1" data-id="element-2878" data-mp="3gzemg4ql5dyq">
      {/* Subcategories */}
      <CollapsibleSection title="Categories" sectionKey="subcategories" data-id="element-2879" data-mp="1b5euds2w69bt">
        <div className="space-y-2" data-id="element-2880" data-mp="1f6gx4iw0p3ij">
          {filterOptions.subcategories.map(subcategory => <label key={subcategory} className="flex items-center cursor-pointer group" data-id="element-2881" data-mp="2o3f6bcaguwwl">
              <input type="checkbox" checked={selectedSubcategories.includes(subcategory)} onChange={() => toggleSubcategory(subcategory)} className={`h-4 w-4 rounded border-gray-300 focus:ring-indigo-500 ${dk.isDark ? 'bg-gray-700 border-gray-600 checked:bg-indigo-600' : 'text-indigo-600'}`} data-id="element-2882" data-mp="2j313c8nq2d5f" />

              <span className={`ml-2 text-sm transition-colors ${dk.isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`} data-id="element-2883" data-mp="32rjqkkp63ghh">
                {subcategory}
              </span>
            </label>)}
        </div>
      </CollapsibleSection>

      {/* Price Ranges */}
      <CollapsibleSection title="Price" sectionKey="price" data-id="element-2884" data-mp="3qip7l36ugetd">
        <div className="space-y-2" data-id="element-2885" data-mp="3lpmkndwko3m5">
          {filterOptions.priceRanges.map(range => <label key={range.label} className="flex items-center cursor-pointer group" data-id="element-2886" data-mp="3t2ltc9gnn2es">
              <input type="checkbox" checked={selectedPriceRanges.includes(range.label)} onChange={() => togglePriceRange(range.label)} className={`h-4 w-4 rounded border-gray-300 focus:ring-indigo-500 ${dk.isDark ? 'bg-gray-700 border-gray-600 checked:bg-indigo-600' : 'text-indigo-600'}`} data-id="element-2887" data-mp="1w13gt2c60jc" />

              <span className={`ml-2 text-sm transition-colors ${dk.isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`} data-id="element-2888" data-mp="ktbs7jqcdbkj">
                {range.label}
              </span>
            </label>)}
        </div>
      </CollapsibleSection>

      {/* Vendors */}
      <CollapsibleSection title="Vendors" sectionKey="vendors" data-id="element-2889" data-mp="yjozrkzbmo9c">
        <div className="space-y-2" data-id="element-2890" data-mp="13lwhkr6u0yjl">
          {filterOptions.vendors.map(vendor => <label key={vendor} className="flex items-center cursor-pointer group" data-id="element-2891" data-mp="1ov0f37y87uup">
              <input type="checkbox" checked={selectedVendors.includes(vendor)} onChange={() => toggleVendor(vendor)} className={`h-4 w-4 rounded border-gray-300 focus:ring-indigo-500 ${dk.isDark ? 'bg-gray-700 border-gray-600 checked:bg-indigo-600' : 'text-indigo-600'}`} data-id="element-2892" data-mp="1jw6yv7rk1pqu" />

              <span className={`ml-2 text-sm transition-colors ${dk.isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`} data-id="element-2893" data-mp="116h3ojjvr1ye">
                {vendor}
              </span>
            </label>)}
        </div>
      </CollapsibleSection>

      {/* Reset Button */}
      <div className="pt-4" data-id="element-2894" data-mp="ndj40l0emnp1">
        <button onClick={resetFilters} className={`w-full py-2 rounded-md font-medium text-sm transition-colors ${dk.isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2895" data-mp="nchifmlriit2">
          Reset Filters
        </button>
      </div>
    </div>;
  return <div className={`min-h-screen ${dk.isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`} data-id="element-2896" data-mp="2k5f4bgcoalu2">
      {/* Mobile filter drawer overlay */}
      {mobileFiltersOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={toggleMobileFilters} aria-hidden="true" data-id="element-2897" data-mp="3ctu6v3embboi"></div>}

      {/* Mobile filter drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 w-80 max-w-[80vw] shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${mobileFiltersOpen ? 'translate-x-0' : '-translate-x-full'} ${dk.isDark ? 'bg-gray-800' : 'bg-white'}`} data-id="element-2898" data-mp="33tnw3pab0pt1">
        <div className={`flex items-center justify-between p-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-2899" data-mp="1wfcez8057tz5">
          <h2 className="text-lg font-bold" data-id="element-2900" data-mp="1lroakf4mrar5">Filters</h2>
          <button onClick={toggleMobileFilters} className={`p-2 rounded-md transition-colors ${dk.isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} aria-label="Close filters" data-id="element-2901" data-mp="1qppzhgoqm4aj">
            <XIcon className="h-5 w-5" data-id="element-2902" data-mp="1gyd8erarji5k" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto h-full pb-32" data-id="element-2903" data-mp="12zvnt2g5mlrv">
          <FilterContent data-id="element-2904" data-mp="132hvr36yj3am" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2905" data-mp="1801a033nng2k">
        {/* Page Header */}
        <div className="mb-8" data-id="element-2906" data-mp="u0ibm20nbj3g">
          <div className="flex items-center gap-3 mb-2" data-id="element-2907" data-mp="u1jqyiop2rqz">
            <div className={`p-2 rounded-lg ${dk.isDark ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`} data-id="element-2908" data-mp="y23mxixj7pbj">
              {icon}
            </div>
            <h1 className="text-3xl font-bold" data-id="element-2909" data-mp="2sfcx7cxwjeuu">{title}</h1>
          </div>
          <p className={`${dk.isDark ? 'text-gray-400' : 'text-gray-600'} max-w-3xl`} data-id="element-2910" data-mp="grgj3wbxlh07">
            {description}
          </p>
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6" data-id="element-2911" data-mp="qh9k63eg6dq7">
          <button onClick={toggleMobileFilters} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md transition-colors ${dk.isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-2912" data-mp="qib3ev5ma6j0">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-2913" data-mp="viqibr5jrm33" />
            <span data-id="element-2914" data-mp="19gnggrzng0jv">Filters</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-2915" data-mp="19e16ymt77az0">
          {/* Desktop Sidebar */}
          <aside className={`hidden lg:block ${sidebarCollapsed ? 'w-14' : 'w-64'} flex-shrink-0 transition-all duration-300`} data-id="element-2916" data-mp="3b7s65ii0ur1k">
            <div className="sticky top-6 overflow-y-auto max-h-[calc(100vh-6rem)]" data-id="element-2917" data-mp="369z0cqkmrz1t">
              <div className="flex justify-between items-center mb-4" data-id="element-2918" data-mp="1a6kk3qo7phno">
                {!sidebarCollapsed && <h2 className="font-bold" data-id="element-2919" data-mp="1f3u7l0s1jj8v">Filters</h2>}
                <button onClick={toggleSidebar} className={`p-2 rounded-md transition-colors ${sidebarCollapsed ? 'mx-auto' : ''} ${dk.isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} aria-label={sidebarCollapsed ? 'Expand filters' : 'Collapse filters'} data-id="element-2920" data-mp="1f6gp9t8nbf4t">
                  {sidebarCollapsed ? <FilterIcon className="h-5 w-5" data-id="element-2921" data-mp="22tyaf5xeneyt" /> : <ChevronLeftIcon className="h-5 w-5" data-id="element-2922" data-mp="27t1gmtp4fr8d" />}
                </button>
              </div>

              {!sidebarCollapsed ? <FilterContent data-id="element-2923" data-mp="2bwplv5qa6aqg" /> : <div className="flex flex-col items-center space-y-6 mt-6" data-id="element-2924" data-mp="2gtfz7lapas86">
                  <FilterIcon className={`h-6 w-6 ${dk.isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-2925" data-mp="56mc0cm1ywtg" />
                </div>}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-2926" data-mp="3sc4weufv2qeh">
            {/* Search and View Toggle */}
            <div className={`rounded-xl shadow-sm p-4 mb-8 ${dk.isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-2927" data-mp="3ndafjy1gfbx0">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-2928" data-mp="jvs4ziuarqsw">
                <div className="relative flex-1" data-id="element-2929" data-mp="f4tpuml1oyr8">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2930" data-mp="f3imgnviht0n" />

                  <input type="text" placeholder={`Search ${title.toLowerCase()}...`} className={`pl-10 pr-4 py-2.5 w-full rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2931" data-mp="17lcqo9hu525v" />
                </div>
                <div className={`flex rounded-lg p-1 ${dk.isDark ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-2932" data-mp="ife771qoi9an">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? dk.isDark ? 'bg-gray-600 text-indigo-400 shadow-sm' : 'bg-white text-indigo-600 shadow-sm' : dk.isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} aria-label="Grid View" data-id="element-2933" data-mp="1a4zga2jan66t">
                    <GridIcon className="h-5 w-5" data-id="element-2934" data-mp="1tsp3dwgaci9p" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? dk.isDark ? 'bg-gray-600 text-indigo-400 shadow-sm' : 'bg-white text-indigo-600 shadow-sm' : dk.isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} aria-label="List View" data-id="element-2935" data-mp="2370h3rwf31py">
                    <ListIcon className="h-5 w-5" data-id="element-2936" data-mp="1k7mnjb2lnszz" />
                  </button>
                </div>
              </div>
            </div>

            {/* Section Header */}
            <div className="flex items-center justify-between mb-6" data-id="element-2937" data-mp="1u6kjgz2cuot0">
              <div className="flex items-center gap-2" data-id="element-2938" data-mp="1u7vjcnsojceu">
                <h2 className="text-xl font-bold" data-id="element-2939" data-mp="1p77v29vu59ob">
                  {filteredProducts.length} Results
                </h2>
              </div>
              <div className={`flex text-sm font-medium rounded-lg p-1 ${dk.isDark ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-2940" data-mp="35nu1dh5u58s7">
                {['All', 'Popular', 'New'].map(tab => {
                const id = tab.toLowerCase();
                return <button key={id} onClick={() => setActiveTab(id)} className={`px-3 py-1.5 rounded-md transition-all ${activeTab === id ? dk.isDark ? 'bg-gray-700 text-indigo-400 shadow-sm' : 'bg-white text-indigo-600 shadow-sm' : dk.isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2941" data-mp="3cncs7cb41coh">
                      {tab}
                    </button>;
              })}
              </div>
            </div>

            {/* Results Grid/List */}
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1'}`} data-id="element-2942" data-mp="33p11zbe5ogwk">
              {filteredProducts.map(product => {
              // GRID VIEW CARD
              if (viewMode === 'grid') {
                return <div key={product.id} className={`group rounded-xl overflow-hidden border transition-all duration-200 hover:shadow-md hover:-translate-y-1 ${dk.isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-2943" data-mp="23n4cyi9f9vdt">
                      {/* Image Area */}
                      <div className="aspect-[4/3] relative bg-gray-200" data-id="element-2944" data-mp="1kqmnk7i1q7kq">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-2945" data-mp="1fz4lfg4ae0y6" />

                        {/* Overlay Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" data-id="element-2946" data-mp="162hzeo0853uk"></div>

                        {/* Price Badge (Top Right) */}
                        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-full border border-white/10" data-id="element-2947" data-mp="x0sappxq8j0h">
                          ${product.price.toFixed(2)}
                        </div>

                        {/* Vendor Badge (Bottom Left) */}
                        <div className="absolute bottom-3 left-3" data-id="element-2948" data-mp="2b0lid3nd7u8k">
                          {product.vendorImage ? <img src={product.vendorImage} alt={product.vendorName} className="w-12 h-12 rounded-lg border-2 border-white object-cover shadow-md" data-id="element-2949" data-mp="226gtuqvpeuw7" /> : <div className="text-white text-xs font-medium drop-shadow-md" data-id="element-2950" data-mp="1x6kq8sqx55h6">
                              by {product.vendorName}
                            </div>}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4" data-id="element-2951" data-mp="14k498g6xyts1">
                        {/* Subcategory Label */}
                        <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-2952" data-mp="zm2m9k6eaaba">
                          {product.subcategory}
                        </div>

                        {/* Name */}
                        <Link to={`/vendor/${product.vendorSlug}`} className={`block text-sm sm:text-base font-bold mb-2 hover:text-indigo-500 transition-colors line-clamp-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2953" data-mp="1nigg1vadrrkg">
                          {product.name}
                        </Link>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1.5 mb-3 h-5 overflow-hidden" data-id="element-2954" data-mp="26are9bu3b1c8">
                          {product.tags.slice(0, 2).map((tag, idx) => <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-full border ${dk.isDark ? 'bg-gray-700/50 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`} data-id="element-2955" data-mp="2dtd1g0t5fv8u" data-mpi={idx}>
                              {tag}
                            </span>)}
                        </div>

                        {/* Bottom Row: Rating */}
                        <div className="flex items-center justify-between mt-auto" data-id="element-2956" data-mp="1v0snrqm0twpm">
                          <div className="flex items-center text-xs font-medium text-amber-400" data-id="element-2957" data-mp="1q1xt9reoyjpo">
                            <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-2958" data-mp="24v1a1wkgktfc" />
                            {product.rating}
                            <span className={`ml-1 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2959" data-mp="1ccdv6yw22amp">
                              ({product.reviewCount})
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>;
              }
              // LIST VIEW CARD
              const isExpanded = expandedProductIds.includes(product.id);
              return <div key={product.id} className={`rounded-xl border transition-all ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-2960" data-mp="3e2urtikgg8aw">
                    {/* Main Row */}
                    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-2961" data-mp="3sqpm33e42kq3">
                      {/* Thumbnail */}
                      <div className="relative flex-shrink-0" data-id="element-2962" data-mp="m59qwt8vmrl">
                        <img src={product.image} alt={product.name} className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-gray-700" data-id="element-2963" data-mp="2hbk5qwl24no0" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0" data-id="element-2964" data-mp="musl3d2zz0m0">
                        <div className="flex items-center flex-wrap gap-2 mb-1" data-id="element-2965" data-mp="mxf01xs7wie9">
                          <Link to={`/vendor/${product.vendorSlug}`} className={`text-lg font-bold hover:text-indigo-500 transition-colors ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2966" data-mp="i30yfdmypf15">
                            {product.name}
                          </Link>
                          <span className={`text-gray-400 ${dk.isDark ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-2967" data-mp="3vavo35j60140">
                            |
                          </span>
                          <span className={`text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2968" data-mp="1tipq2ib5v28a">
                            {product.subcategory}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm" data-id="element-2969" data-mp="2131pkh960zc7">
                          <span className={`font-bold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2970" data-mp="210x9z21wmbw3">
                            ${product.price.toFixed(2)}
                          </span>
                          <div className="flex items-center gap-1 text-amber-400" data-id="element-2971" data-mp="1e3vqjwl940l3">
                            <StarIcon className="h-3.5 w-3.5 fill-current" data-id="element-2972" data-mp="1i3w47f0j07r3" />
                            <span className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-2973" data-mp="1n3ii7qo6hs0y">
                              {product.rating}
                            </span>
                            <span className={`text-xs ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2974" data-mp="33hs5sikljcqq">
                              ({product.reviewCount})
                            </span>
                          </div>
                          <div className={`flex items-center ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2975" data-mp="bcaz2fe6b6wj">
                            <span className="mr-1" data-id="element-2976" data-mp="ka4snikjzmg0">by</span>
                            <span className="font-medium" data-id="element-2977" data-mp="z7niko8yw5p1">
                              {product.vendorName}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action */}
                      <button onClick={() => toggleProductExpand(product.id)} className={`p-2 rounded-full transition-colors self-end sm:self-center ${dk.isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-2978" data-mp="182bngdamrldb">
                        {isExpanded ? <ChevronUpIcon className="h-5 w-5" data-id="element-2979" data-mp="1wmf0o6zf898b" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-2980" data-mp="21kqc2m5d6rsn" />}
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && <div className={`border-t p-4 sm:p-6 ${dk.isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`} data-id="element-2981" data-mp="1rkpqsunxq1lt">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-2982" data-mp="1in5kh7b7tf4c">
                          <div className="lg:col-span-2 space-y-4" data-id="element-2983" data-mp="13y113msop4un">
                            <div data-id="element-2984" data-mp="18v9l7sxe7iyj">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2985" data-mp="18wkm9kzgecly">
                                Description
                              </h4>
                              <p className={`text-sm leading-relaxed ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2986" data-mp="31lbuz8t6jth4">
                                {product.description}
                              </p>
                            </div>
                            <div data-id="element-2987" data-mp="2mwp6aghnxpd8">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2988" data-mp="2mtb7fi87jyaj">
                                Tags
                              </h4>
                              <div className="flex flex-wrap gap-2" data-id="element-2989" data-mp="3aj5fznlhv0oz">
                                {product.tags.map(tag => <span key={tag} className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300 border-indigo-800/50' : 'bg-indigo-50 text-indigo-700 border-indigo-100'}`} data-id="element-2990" data-mp="1z5tl427ppsey">
                                    {tag}
                                  </span>)}
                              </div>
                            </div>
                          </div>
                          <div className={`lg:border-l pl-0 lg:pl-8 space-y-4 ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-2991" data-mp="ofnal5wyelpi">
                            <div className="space-y-2" data-id="element-2992" data-mp="ydusps2zb9t3">
                              {product.shipping && <div className="flex items-center text-sm" data-id="element-2993" data-mp="12bir553vrkpq">
                                  <TruckIcon className={`h-4 w-4 mr-2 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2994" data-mp="17bn9l0ocmpil" />

                                  <span className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-2995" data-mp="394bdlc8thgzf">
                                    {product.shipping}
                                  </span>
                                </div>}
                              {product.location && <div className="flex items-center text-sm" data-id="element-2996" data-mp="rvrld7oi32v7">
                                  <MapPinIcon className={`h-4 w-4 mr-2 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2997" data-mp="rxumamr6ygep" />

                                  <span className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-2998" data-mp="10y9abtvji9lw">
                                    {product.location}
                                  </span>
                                </div>}
                            </div>
                            <Link to={`/vendor/${product.vendorSlug}`} className={`block w-full text-center py-2 rounded-lg font-medium transition-colors ${dk.isDark ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`} data-id="element-2999" data-mp="3q73ah1w2w3g2">
                              View Vendor Profile
                            </Link>
                          </div>
                        </div>
                      </div>}
                  </div>;
            })}
            </div>

            {/* Empty State */}
            {filteredProducts.length === 0 && <div className={`text-center py-16 rounded-xl border border-dashed ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`} data-id="element-3000" data-mp="27heiksgbqnqr">
                <ShoppingBagIcon className={`h-12 w-12 mx-auto mb-4 ${dk.isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-3001" data-mp="2h6y2m1frwnn7" />

                <h3 className={`text-lg font-medium mb-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3002" data-mp="2l81lkwt4azsn">
                  No products found
                </h3>
                <p className={`max-w-md mx-auto ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3003" data-mp="1thx2d1h3git0">
                  Try adjusting your search or filters to find what you're
                  looking for.
                </p>
                <button onClick={resetFilters} className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors" data-id="element-3004" data-mp="38ynhxqtrevmt">
                  Clear Filters
                </button>
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default MerchCategoryPage;